'use client'

import React, {
  useMemo,
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
  useRef,
} from 'react'
import { ReadyState } from 'react-use-websocket'

import { useAuth, INITIAL_USER_ID } from '@dc-shared-web/providers/AuthProvider'

import useChatWebSocket from '@dc-shared-web/providers/ChatProvider/useChatWebSocket'
import {
  ChatDefaultsResponse,
  ChatError,
  ChatErrorType,
  ChatExplainResponse,
  ChatHistoryResponse,
  ChatMessage,
  ChatQueryResponse,
  ChatResponse,
  ChatServiceState,
  ChatSettings,
  ChatTextReference,
  ChatUser,
  ChatUsersResponse,
} from '@dc-shared-web/providers/ChatProvider/types'
import {
  chatUsersRequest,
  defaultsRequest,
  explainRequest,
  formatMessagesFromHistory,
  historyRequest,
  queryRequest,
  stopRequest,
  conversationMessagesUpdater,
} from '@dc-shared-web/providers/ChatProvider/helpers'
import { useAPIToken } from '@dc-shared-web/providers/APITokenProvider'
import { User, useFSTrackEvent } from '@dc-shared-web/hooks'
import { FS } from '@dc-shared-web/lib/trackingConstants'
import { v4 as uuidv4 } from 'uuid'

export type ChatContextType = {
  hasLoaded: boolean
  currentUserId?: number
  users: ChatUser[]
  messages: ChatMessage[]
  currentSettings?: ChatSettings
  defaultSettings?: ChatSettings
  isReceiving: boolean
  errors: ChatError[]
  serviceStatus: ChatServiceState
  citationEnabled: boolean
  isAgenticChat: boolean
  explain: (text: string, references: ChatTextReference[]) => void
  stop: () => void
  query: (text: string) => void
  switchUser: (userId: number) => void
  setCurrentSettings: (settings: ChatSettings) => void
  setServiceStatus: (status: ChatServiceState) => void
  setErrorState: () => void
  clearDebugState: () => void
  toggleWorking: (working: boolean) => void
  queryInProgress: boolean
  // Pagination support
  hasMoreMessages: boolean
  loadMoreMessages: () => void
  isLoadingMoreMessages: boolean
}

type ChatProviderProps = {
  url: string
  crunchUUID: string
  documentGuid?: string
  token?: string
  user?: User
  withCitations?: boolean
  enablePagination?: boolean
}

const ChatContext = createContext<ChatContextType>({
  hasLoaded: false,
  users: [],
  messages: [],
  isReceiving: false,
  errors: [],
  serviceStatus: ChatServiceState.UNAVAILABLE,
  citationEnabled: false,
  isAgenticChat: false,
  queryInProgress: false,
  hasMoreMessages: false,
  isLoadingMoreMessages: false,
  explain: () => {},
  stop: () => {},
  query: () => {},
  switchUser: () => {},
  setCurrentSettings: () => {},
  setServiceStatus: () => {},
  setErrorState: () => {},
  clearDebugState: () => {},
  toggleWorking: () => {},
  loadMoreMessages: () => {},
})

export const ChatProvider = ({
  url,
  crunchUUID,
  documentGuid,
  children,
  token,
  user,
  withCitations = false,
  enablePagination = false,
}: ChatProviderProps & {
  children: React.ReactNode
}) => {
  const { user: authUser } = useAuth()
  const { token: apiToken } = useAPIToken()
  const { trackEvent: trackFSEvent } = useFSTrackEvent()

  const [hasLoaded, setHasLoaded] = useState(false)
  const [users, setUsers] = useState<ChatUser[]>([])
  const [currentUserId, setCurrentUserId] = useState<number | undefined>(
    authUser?.id === INITIAL_USER_ID ? user?.id : authUser.id
  )
  const [messages, setMessages] = useState<ChatMessage[]>([])
  const [currentSettings, setCurrentSettings] = useState<
    ChatSettings | undefined
  >()
  const [defaultSettings, setDefaultSettings] = useState<
    ChatSettings | undefined
  >()
  const [receivingCount, setReceivingCount] = useState(0)
  const [errors, setErrors] = useState<ChatError[]>([])
  const [serviceStatus, setServiceStatus] = useState<ChatServiceState>(
    ChatServiceState.BUSY
  )
  const [queryInProgress, setQueryInProgress] = useState(false)

  // Pagination state
  const [hasMoreMessages, setHasMoreMessages] = useState(false)
  const [isLoadingMoreMessages, setIsLoadingMoreMessages] = useState(false)
  const [loadedConversationsCount, setLoadedConversationsCount] = useState(0)

  const isLoading = useRef(false)

  // Determine if we're using the agentic chat URL
  const isAgenticChat = useMemo(() => {
    // The URL comes in format like "wss://agentic.example.com/ws"
    // We need to check if it contains "agentic" in the hostname
    return url.toLowerCase().includes('agentic')
  }, [url])

  const requestContext = useMemo(
    () => ({
      api_token: (token || apiToken) ?? '',
      uuid: crunchUUID,
      user_id: currentUserId,
      doc_guid: documentGuid,
    }),
    [crunchUUID, currentUserId, token, apiToken, documentGuid]
  )

  const messageHandler = useCallback(
    (data: ChatQueryResponse | ChatExplainResponse) => {
      if (
        (data.payload.status === 'starting' &&
          [
            '<locating relevant text>',
            '<verifying existence of document index>',
          ].includes(data.payload.message)) ||
        // Specs have some new non-'starting'-status loading messages
        data.payload.message.startsWith('Analyzing document...') ||
        data.payload.message.startsWith('Reading document pages')
      ) {
        setReceivingCount((count) => count + 1)
      } else if (data.payload.status === 'response-delayed') {
        setServiceStatus(ChatServiceState.BUSY)
      } else if (data.payload.status !== 'starting') {
        setMessages(conversationMessagesUpdater(data, currentSettings))
        if (data.payload.status === 'complete') {
          setReceivingCount(0)
          setQueryInProgress(false)
        }
      }
    },
    [currentSettings]
  )

  const handlers = useMemo(
    () => ({
      onHistory: (data: ChatHistoryResponse) => {
        const result = formatMessagesFromHistory(data)
        const isLoadingMore = isLoadingMoreMessages

        if (isLoadingMore) {
          // Prepend older messages when loading more (older messages go above newer ones)
          setMessages((prevMessages) => [...result.messages, ...prevMessages])
          setIsLoadingMoreMessages(false)
        } else {
          // Replace messages for initial load
          setMessages(result.messages)
          setHasLoaded(true)
          isLoading.current = false
        }

        // Update pagination state
        if (result.pagination) {
          if (isLoadingMore) {
            // When loading more, increment the count by the number of new conversations
            setLoadedConversationsCount(
              (prev) => prev + result.conversationCount
            )
          } else {
            // For initial load, use the pagination data directly
            setLoadedConversationsCount(
              result.pagination.offset + result.conversationCount
            )
          }
          setHasMoreMessages(result.pagination.has_more)
        } else {
          // Legacy format - no more messages available
          setHasMoreMessages(false)
          setLoadedConversationsCount(result.conversationCount)
        }
      },
      onDefaults: (data: ChatDefaultsResponse) => {
        setCurrentSettings((settings) => ({
          ...(settings ? settings : data.payload),
          ...(withCitations && { with_citations: true }),
        }))
        setDefaultSettings(data.payload)
      },
      onQuery: (data: ChatQueryResponse) => {
        messageHandler(data)
      },
      onExplain: (data: ChatExplainResponse) => {
        messageHandler(data)
      },
      onChatUsers(data: ChatUsersResponse) {
        setUsers(data.payload as ChatUser[])
      },
      onErrors: (errors: ChatError[], data?: ChatResponse) => {
        setReceivingCount(0)
        setErrors(errors)
        setQueryInProgress(false)
        if (data) {
          trackFSEvent(FS.CHAT_ERROR, data)
        }
      },
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [messageHandler, isLoadingMoreMessages]
  )

  const { sendRequest, readyState } = useChatWebSocket(url, handlers)

  const explain = useCallback(
    (text: string, references: ChatTextReference[]) => {
      trackFSEvent(FS.CHAT_EXPLAIN, {
        query: text || 'Unknown',
      })
      sendRequest(explainRequest(requestContext, text, references))
    },
    [sendRequest, requestContext, trackFSEvent]
  )

  const stop = useCallback(() => {
    sendRequest(stopRequest(requestContext))
    setReceivingCount(0)
    setQueryInProgress(false)
  }, [sendRequest, requestContext])

  const query = useCallback(
    (text: string) => {
      if (requestContext) {
        setQueryInProgress(true)
        trackFSEvent(FS.CHAT_QUERY, {
          query: text || 'Unknown',
        })
        sendRequest(
          queryRequest(requestContext, text, crunchUUID, currentSettings)
        )
        setMessages((messages) => [
          ...messages,
          {
            renderId: uuidv4(),
            type: 'human',
            text,
            settings: {},
          },
        ])
      }
    },
    [sendRequest, requestContext, crunchUUID, currentSettings, trackFSEvent]
  )

  const loadMoreMessages = useCallback(() => {
    if (
      enablePagination &&
      hasMoreMessages &&
      !isLoadingMoreMessages &&
      requestContext
    ) {
      setIsLoadingMoreMessages(true)
      sendRequest(
        historyRequest(requestContext, {
          limit: 5,
          offset: loadedConversationsCount,
        })
      )
    }
  }, [
    enablePagination,
    hasMoreMessages,
    isLoadingMoreMessages,
    requestContext,
    sendRequest,
    loadedConversationsCount,
  ])

  const value = useMemo(
    () => ({
      hasLoaded,
      currentUserId,
      users,
      messages,
      currentSettings,
      defaultSettings,
      isReceiving: receivingCount > 0,
      queryInProgress,
      errors,
      serviceStatus,
      citationEnabled: withCitations,
      isAgenticChat,
      hasMoreMessages,
      isLoadingMoreMessages,
      explain,
      stop,
      query,
      loadMoreMessages,
      switchUser: setCurrentUserId,
      setCurrentSettings: (settings: ChatSettings) => {
        setCurrentSettings({
          ...settings,
          ...(withCitations && { with_citations: true }),
        })
      },
      setServiceStatus,
      setErrorState: () => {
        setServiceStatus(ChatServiceState.OK)
        setErrors([
          {
            type: ChatErrorType.Default,
            message: '',
          },
        ])
      },
      clearDebugState: () => {
        setServiceStatus(ChatServiceState.OK)
        setErrors([])
      },
      toggleWorking: (working: boolean) => {
        setReceivingCount(working ? 1 : 0)
      },
    }),
    [
      hasLoaded,
      currentUserId,
      users,
      messages,
      currentSettings,
      defaultSettings,
      receivingCount,
      errors,
      serviceStatus,
      hasMoreMessages,
      isLoadingMoreMessages,
      explain,
      stop,
      query,
      loadMoreMessages,
      withCitations,
      queryInProgress,
      isAgenticChat,
    ]
  )

  useEffect(() => {
    setCurrentUserId(authUser?.id === INITIAL_USER_ID ? user?.id : authUser.id)
  }, [user, authUser.id])

  useEffect(() => {
    if (readyState === ReadyState.CLOSED) {
      setServiceStatus(ChatServiceState.UNAVAILABLE)
    } else if (readyState === ReadyState.OPEN) {
      setServiceStatus(ChatServiceState.OK)
    }
  }, [readyState])

  // Effect 1: Handle context changes (user switch, version switch)
  useEffect(() => {
    if (!hasLoaded) {
      return // Early return - only run if we've already loaded once
    }

    // Stop any ongoing requests to prevent responses in wrong context
    if (receivingCount > 0 || queryInProgress) {
      sendRequest(stopRequest(requestContext))
    }

    setHasLoaded(false)
    setMessages([])
    setReceivingCount(0)
    setQueryInProgress(false)
    // Reset pagination state
    setHasMoreMessages(false)
    setIsLoadingMoreMessages(false)
    setLoadedConversationsCount(0)
    sendRequest(
      enablePagination
        ? historyRequest(requestContext, { limit: 5, offset: 0 })
        : historyRequest(requestContext)
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUserId, documentGuid, enablePagination])

  // Effect 2: Handle initial load only
  useEffect(() => {
    if (readyState === ReadyState.OPEN && !hasLoaded && !isLoading.current) {
      isLoading.current = true
      setHasLoaded(false)
      setMessages([])
      // Reset pagination state for initial load
      setHasMoreMessages(false)
      setIsLoadingMoreMessages(false)
      setLoadedConversationsCount(0)
      sendRequest(
        enablePagination
          ? historyRequest(requestContext, { limit: 5, offset: 0 })
          : historyRequest(requestContext)
      )
      sendRequest(chatUsersRequest(requestContext))
      sendRequest(defaultsRequest(requestContext))
    }
  }, [sendRequest, readyState, hasLoaded, requestContext, enablePagination])

  return <ChatContext.Provider value={value}>{children}</ChatContext.Provider>
}

export const useChat = (): ChatContextType => {
  return useContext(ChatContext)
}

export const withChatProvider = <T extends {}>(
  Component: React.ComponentType<T>
) => {
  const newComponent = ({
    chatURL,
    crunchUUID,
    documentGuid,
    token,
    withCitations,
    user,
    enablePagination,
    ...props
  }: {
    chatURL: string
    crunchUUID: string
    documentGuid?: string
    token?: string
    withCitations?: boolean
    user?: User
    enablePagination?: boolean
  } & React.ComponentProps<React.ComponentType<T>>) => (
    <ChatProvider
      url={chatURL}
      crunchUUID={crunchUUID}
      documentGuid={documentGuid}
      token={token}
      withCitations={withCitations}
      user={user}
      enablePagination={enablePagination}
    >
      <Component
        {...(props as unknown as React.ComponentProps<React.ComponentType<T>>)}
      />
    </ChatProvider>
  )

  newComponent.displayName = `withChatProvider(${Component.displayName})`

  return newComponent
}
